/**
 * Step 2 — the announcement's content.
 *
 * The fields and the JSON are two views of one document and are kept in sync both ways: editing a
 * field rewrites the JSON, and pasting valid JSON repopulates the fields. Invalid JSON is left
 * exactly as typed with the parser's message in the status — reverting someone's text mid-edit is
 * worse than showing them the error.
 *
 * With several tenants each keeps its own copy of the content; switching the "editando o conteúdo
 * de" chip swaps the whole document.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { TenantFlag } from '../../../Hooks/useTenantFlags';
import type { Language, WhatsNewFormItem } from '../../../Types/WhatsNewForm.types';
import type { WhatsNewContent } from '../../../Utils/contentJson';
import { LANGUAGES, getContentProblems } from '../../../Utils/contentJson';
import { FlagsBar } from '../Components/FlagsBar';
import { ItemCard } from '../Components/ItemCard';
import { Switch } from '@/Shared/components/Switch/Switch';
import { Tag } from '@/Shared/components/Tag/Tag';

const LANGUAGE_NAMES: Record<Language, string> = {
  pt: 'Português',
  en: 'Inglês',
  es: 'Espanhol',
};

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
`;

/*
  Anchored to the very top of the scroll, not below the page header.

  It used to offset by 64px to clear that header, but the header is wrapped in a motion.div of its
  own height — a sticky element only sticks inside its parent's box, so the header scrolls away
  immediately and the offset left a 64px see-through gap with the form showing through it.

  The padding is on the block rather than around it so that the background covers the whole strip
  while it is pinned; a margin there would leave the same gap.
*/
const stickyStyle = css`
  position: sticky;
  top: 0;
  z-index: var(--z-sticky);
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding: var(--space-m) 0 var(--space-s);
`;

const titleRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-base);
  flex-wrap: wrap;
`;

const titleBlockStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  min-width: 0;
`;

const titleStyle = css`
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
`;

const subtitleStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const controlsStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-wrap: wrap;
  padding-bottom: var(--space-s);
  border-bottom: 1px solid var(--color-divider);
`;

const dividerStyle = css`
  width: 1px;
  height: 22px;
  background: var(--color-divider);
`;

const miniLabelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const statusStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-2xs);
  max-width: 320px;
`;

const tenantRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const jsonAreaStyle = css`
  width: 100%;
  height: 560px;
  padding: var(--space-m);
  border-radius: var(--radius-m);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1.6;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const sectionTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const sectionHintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const monoLabelStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const textareaStyle = css`
  width: 100%;
  padding: var(--space-s);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const selectStyle = css`
  height: 36px;
  padding: 0 var(--space-s);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-control);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
`;

const switchRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

const switchTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

/*
  What is still missing, listed rather than hidden behind a disabled button. The BFF rejects the
  whole payload when any language is blank, and before this the person only found out at the last
  step, as a wall of `items.1.translations.2.title should not be empty`.
*/
const pendingStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
`;

const pendingTitleStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--gl-yellow-dark);
`;

const pendingListStyle = css`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const itemsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

export interface PageOption {
  value: string;
  label: string;
}

export interface StepContentProps {
  content: WhatsNewContent;
  jsonText: string;
  jsonError: string | null;
  tab: 'fields' | 'json';
  language: Language;
  /** Tenants whose content can be edited; more than one shows the copy control. */
  tenantSlugs: string[];
  activeTenant: string;
  environmentKey?: string | null;
  flags: TenantFlag[];
  isLoadingFlags: boolean;
  isMissingKey: boolean;
  /** Pages of the active tenant, for the redirect select. */
  pageOptions: PageOption[];
  onTabChange: (tab: 'fields' | 'json') => void;
  onLanguageChange: (language: Language) => void;
  onActiveTenantChange: (slug: string) => void;
  onJsonChange: (raw: string) => void;
  onContentChange: (next: WhatsNewContent) => void;
  onCopyToOthers: () => void;
  onPreview: () => void;
  onShowSchema: () => void;
  onReloadFlags: () => void;
  onOpenBU: () => void;
}

export const StepContent = ({
  content,
  jsonText,
  jsonError,
  tab,
  language,
  tenantSlugs,
  activeTenant,
  environmentKey,
  flags,
  isLoadingFlags,
  isMissingKey,
  pageOptions,
  onTabChange,
  onLanguageChange,
  onActiveTenantChange,
  onJsonChange,
  onContentChange,
  onCopyToOthers,
  onPreview,
  onShowSchema,
  onReloadFlags,
  onOpenBU,
}: StepContentProps): JSX.Element => {
  const actionParams = content.actionParams ?? {};

  const setTranslationField = (
    field: 'welcomeMessage' | 'subtitle' | 'footer' | 'confirmButtonText',
    value: string,
  ) => {
    onContentChange({ ...content, [field]: { ...content[field], [language]: value } });
  };

  const setActionParams = (patch: Record<string, unknown>) => {
    const next = { ...actionParams, ...patch };
    // Drop empty entries so the JSON does not grow keys nobody set.
    const cleaned = Object.fromEntries(
      Object.entries(next).filter(
        ([, value]) => value !== '' && value !== false && value !== null && value !== undefined,
      ),
    );
    onContentChange({
      ...content,
      actionParams: Object.keys(cleaned).length > 0 ? cleaned : undefined,
    });
  };

  const problems = getContentProblems(content);

  const updateItem = (index: number, next: WhatsNewFormItem) => {
    onContentChange({
      ...content,
      items: content.items.map((item, position) => (position === index ? next : item)),
    });
  };

  const textFields: {
    key: 'welcomeMessage' | 'subtitle' | 'footer' | 'confirmButtonText';
    rows: number;
  }[] = [
    { key: 'welcomeMessage', rows: 2 },
    { key: 'subtitle', rows: 3 },
    { key: 'footer', rows: 3 },
    { key: 'confirmButtonText', rows: 1 },
  ];

  return (
    <div className={wrapStyle}>
      <div className={stickyStyle}>
        <div className={titleRowStyle}>
          <div className={titleBlockStyle}>
            <h2 className={titleStyle}>Conteúdo do anúncio</h2>
            <p className={subtitleStyle}>
              Edite pelos campos ou cole o JSON pronto. Os dois lados ficam sempre iguais.
            </p>
          </div>
          <div className={spacerStyle} />
          <UTButton
            fitContent
            variant='tertiary'
            size='small'
            text='Ver schema'
            icon='Misc.Lightbulb'
            onClick={onShowSchema}
          />
          <UTButton
            fitContent
            variant='secondary'
            size='small'
            text='Pré-visualizar'
            icon='Misc.Eye'
            onClick={onPreview}
          />
        </div>

        <div className={controlsStyle}>
          <Tag
            tone='primary'
            selected={tab === 'fields'}
            onClick={() => {
              onTabChange('fields');
            }}
          >
            Campos
          </Tag>
          <Tag
            tone='primary'
            selected={tab === 'json'}
            onClick={() => {
              onTabChange('json');
            }}
          >
            JSON
          </Tag>

          <span className={dividerStyle} />

          <span className={miniLabelStyle}>Idioma</span>
          {LANGUAGES.map(entry => (
            <Tag
              key={entry}
              tone='primary'
              selected={language === entry}
              onClick={() => {
                onLanguageChange(entry);
              }}
            >
              {LANGUAGE_NAMES[entry]}
            </Tag>
          ))}

          <div className={spacerStyle} />

          {jsonError ? (
            <span className={statusStyle} style={{ color: 'var(--color-danger)' }}>
              <GLIcon variant='Notifications.Warning' size={14} fill='var(--color-danger)' />
              JSON inválido: {jsonError}
            </span>
          ) : (
            <span className={statusStyle} style={{ color: 'var(--color-success)' }}>
              <GLIcon variant='Communication.CheckCircle' size={14} fill='var(--color-success)' />
              JSON válido — {content.items.length} {content.items.length === 1 ? 'item' : 'itens'},
              3 idiomas
            </span>
          )}
        </div>

        {tenantSlugs.length > 1 ? (
          <div className={tenantRowStyle}>
            <span className={miniLabelStyle}>Editando o conteúdo de</span>
            {tenantSlugs.map(slug => (
              <Tag
                key={slug}
                tone='primary'
                selected={slug === activeTenant}
                onClick={() => {
                  onActiveTenantChange(slug);
                }}
              >
                {slug}
              </Tag>
            ))}
            <div className={spacerStyle} />
            <UTButton
              fitContent
              variant='tertiary'
              size='small'
              text='Copiar este JSON para os outros'
              icon='Communication.CopySimple'
              onClick={onCopyToOthers}
            />
          </div>
        ) : null}

        <FlagsBar
          tenantSlug={activeTenant}
          environmentKey={environmentKey}
          flagCount={flags.length}
          isLoading={isLoadingFlags}
          isMissingKey={isMissingKey}
          onReload={onReloadFlags}
          onOpenBU={onOpenBU}
        />
      </div>

      {problems.length > 0 ? (
        <div className={pendingStyle}>
          <span className={pendingTitleStyle}>
            <GLIcon variant='Notifications.Warning' size={14} fill='var(--gl-yellow-dark)' />
            Falta preencher antes de continuar
          </span>
          <ul className={pendingListStyle}>
            {problems.map(problem => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {tab === 'json' ? (
        <textarea
          id='whats-new-json'
          className={jsonAreaStyle}
          spellCheck={false}
          value={jsonText}
          aria-label='JSON do anúncio'
          onChange={event => {
            onJsonChange(event.target.value);
          }}
        />
      ) : (
        <>
          <section className={sectionStyle}>
            <span className={sectionTitleStyle}>Texto do modal</span>
            {textFields.map(field => (
              <div key={field.key} className={fieldStyle}>
                <span className={monoLabelStyle}>{field.key}</span>
                <textarea
                  className={textareaStyle}
                  rows={field.rows}
                  value={content[field.key][language]}
                  onChange={event => {
                    setTranslationField(field.key, event.target.value);
                  }}
                />
              </div>
            ))}
          </section>

          <section className={sectionStyle}>
            <span className={sectionTitleStyle}>Ação do botão</span>
            <span className={sectionHintStyle}>
              O que acontece quando a pessoa clica no botão de confirmar. Tudo opcional.
            </span>

            <div className={fieldStyle}>
              <span className={monoLabelStyle}>Redirecionar para a página</span>
              <select
                className={selectStyle}
                value={(actionParams as { redirect_to?: string }).redirect_to ?? ''}
                onChange={event => {
                  setActionParams({ redirect_to: event.target.value });
                }}
              >
                <option value=''>Sem redirecionamento</option>
                {pageOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={switchRowStyle}>
              <Switch
                checked={Boolean((actionParams as { show_tour?: boolean }).show_tour)}
                aria-label='Iniciar o tour da página'
                onChange={next => {
                  setActionParams({ show_tour: next });
                }}
              />
              <div className={switchTextStyle}>
                <span className={sectionTitleStyle}>Iniciar o tour da página</span>
                <span className={sectionHintStyle}>Dispara o tour ativo da página de destino.</span>
              </div>
            </div>

            <div className={switchRowStyle}>
              <Switch
                checked={Boolean((actionParams as { open_filter?: boolean }).open_filter)}
                aria-label='Abrir o filtro'
                onChange={next => {
                  setActionParams({ open_filter: next });
                }}
              />
              <div className={switchTextStyle}>
                <span className={sectionTitleStyle}>Abrir o filtro</span>
                <span className={sectionHintStyle}>
                  Abre o painel de filtros ao chegar na página.
                </span>
              </div>
            </div>
          </section>

          <section className={sectionStyle}>
            <span className={sectionTitleStyle}>Itens</span>
            <div className={itemsStyle}>
              {content.items.map((item, index) => (
                <ItemCard
                  key={index}
                  item={item}
                  index={index}
                  language={language}
                  flags={flags}
                  isLoadingFlags={isLoadingFlags}
                  isMissingKey={isMissingKey}
                  environmentKey={environmentKey}
                  canRemove={content.items.length > 1}
                  onChange={updateItem}
                  onRemove={position => {
                    onContentChange({
                      ...content,
                      items: content.items
                        .filter((_, at) => at !== position)
                        .map((remaining, at) => ({ ...remaining, order: at })),
                    });
                  }}
                  onOpenBU={onOpenBU}
                />
              ))}
            </div>
            <UTButton
              id='whats-new-add-item'
              variant='secondary'
              text='Adicionar item'
              icon='Arrows.Plus'
              onClick={() => {
                onContentChange({
                  ...content,
                  items: [
                    ...content.items,
                    {
                      icon: 'Communication.Megaphone',
                      color: '#0033FF',
                      title: '',
                      body: '',
                      order: content.items.length,
                      translations: {
                        pt: { title: '', body: '' },
                        en: { title: '', body: '' },
                        es: { title: '', body: '' },
                      },
                      privileges: [],
                    },
                  ],
                });
              }}
            />
          </section>
        </>
      )}
    </div>
  );
};
